import { useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { EventsSubTabs } from '@/components/events/EventsSubTabs'
import { useEvents, useLivePace } from '@/hooks/useEvents'
import { addDaysIso, fmtDate, fmtTime, todayIso } from '@/lib/dates'
import { ApiError } from '@/lib/api/rawFetch'
import { SELL_THROUGH_TEXT, sellThroughLevel } from '@/lib/sellThrough'

// Shared status-pill base (colour pass). Kept at full pill size: this screen
// is read on a phone mid-show.
const PILL_BASE =
  'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap'

// View D skeleton (Phase 1): live sales pace during an active event.
// Polls every 5 minutes; mobile-first single column so Gina can read it
// on her phone at the booth. The mid-event transfer call trigger (tel:
// link) arrives in Phase 3.
export default function EventsLive() {
  const { data: allEvents = [] } = useEvents()
  const [eventId, setEventId] = useState<number | undefined>()

  const today = todayIso()
  const activeEvents = useMemo(
    () => allEvents.filter((e) =>
      e.event_date <= today && today < addDaysIso(e.event_date, e.event_duration_days)
    ),
    [allEvents, today]
  )

  useEffect(() => {
    if (eventId == null && activeEvents.length > 0) setEventId(activeEvents[0].id)
  }, [activeEvents, eventId])

  const { data, error, isFetching, refetch, dataUpdatedAt } = useLivePace(eventId)
  const notActive = error instanceof ApiError && error.status === 409

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4 max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-base font-semibold">Live Sales Pace</h1>
          <div className="flex-1" />
          <EventsSubTabs />
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select
            className="h-7 border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs focus:outline-none focus:border-[var(--brand)]"
            value={eventId ?? ''}
            onChange={(e) => setEventId(Number(e.target.value) || undefined)}
          >
            {allEvents.length === 0 && <option value="">— no events —</option>}
            {(activeEvents.length > 0 ? activeEvents : allEvents).map((e) => (
              <option key={e.id} value={e.id}>
                {e.location_name} · {fmtDate(e.event_date)}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => refetch()}
            disabled={isFetching || eventId == null}
          >
            <RefreshCw className={`mr-1 h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {data && (
            <span className="text-xs text-[var(--mute)]">
              as of {fmtTime(new Date(dataUpdatedAt))}
            </span>
          )}
        </div>

        {activeEvents.length === 0 && (
          <p className="text-xs text-[var(--mute)] mb-2">
            No event is running today — pick a past event to preview the view.
          </p>
        )}

        {notActive ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
            {error.message}
          </div>
        ) : error ? (
          <div className="border border-[var(--status-rejected-bg)] bg-[var(--status-rejected-bg)] px-2 py-8 text-center text-xs text-[var(--status-rejected-fg)]">
            {error instanceof Error ? error.message : 'Failed to load live data'}
          </div>
        ) : !data ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
            {eventId == null ? 'Select an event.' : 'Loading live sales…'}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {data.products.map((p) => (
              <div
                key={p.variant_id}
                className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-3 py-2 flex items-center gap-3 text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.product_title ?? p.sku ?? p.variant_id}</div>
                  <div className="text-[var(--mute)] truncate">
                    {p.variant_title}{p.sku ? ` · ${p.sku}` : ''}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div>
                    {p.qty_sold_so_far}/{p.qty_available || '?'} sold
                  </div>
                  <div className="text-[var(--mute)]">{p.sales_pace_per_hour}/hr</div>
                </div>
                <div className="w-24 text-right">
                  {p.sold_out ? (
                    // Red, not amber: on the live screen a sell-out is a
                    // customer who can't buy right now (owner decision).
                    <span className={`${PILL_BASE} bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]`}>
                      Sold out
                    </span>
                  ) : p.stockout_risk ? (
                    // Amber: forward-looking attention, not a failure yet.
                    <span className={`${PILL_BASE} bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]`}>
                      ⚠ {p.projected_sell_through_pct}%
                    </span>
                  ) : p.slow_mover ? (
                    <span className={`${PILL_BASE} bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]`}>
                      Slow
                    </span>
                  ) : p.projected_sell_through_pct != null ? (
                    <span
                      className={`tabular-nums ${SELL_THROUGH_TEXT[sellThroughLevel(p.projected_sell_through_pct)]}`}
                    >
                      → {p.projected_sell_through_pct}%
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
            {data.products.length === 0 && (
              <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)]">
                No transfers or sales yet today.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
